import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpApiService } from '@recruitee/http';
import type { Position } from '@recruitee/http-types';
import { ID } from '@recruitee/ngrx';
import type { TalentPoolData } from '@recruitee/talent-pools-types';

import {
  TalentPoolsBriefFetchCollectionResponse,
  TalentPoolsFetchCollectionResponse,
  TalentPoolsResponse,
  TalentPoolsScope,
} from './talent-pool.types';

@Injectable({ providedIn: 'root' })
export class TalentPoolsHttpClient {
  constructor(protected httpApi: HttpApiService) {}

  public fetchCollection(
    scope: TalentPoolsScope = TalentPoolsScope.Default,
  ): Observable<TalentPoolsFetchCollectionResponse> {
    return this.httpApi.get(`talent_pools?scope=${scope}`);
  }

  public fetchCollectionBrief(): Observable<TalentPoolsBriefFetchCollectionResponse> {
    return this.httpApi.get(`talent_pools?viewMode=brief`);
  }

  public fetchOne(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.get(`talent_pools/${id}`);
  }

  public update(talentPool: TalentPoolData): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`talent_pools/${talentPool.id}`, null, {
      talentPool,
    });
  }

  public create(talentPool: TalentPoolData): Observable<TalentPoolsResponse> {
    return this.httpApi.post('/talent_pools', {}, { talentPool });
  }

  public duplicate(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`talent_pools/${id}/duplicate`);
  }

  public delete(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.delete(`talent_pools/${id}`);
  }

  public archive(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`talent_pools/${id}/archive`);
  }

  public retrieve(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`talent_pools/${id}/draft`);
  }

  public follow(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`/talent_pools/${id}/follow`);
  }

  public unfollow(id: ID): Observable<TalentPoolsResponse> {
    return this.httpApi.patch(`/talent_pools/${id}/unfollow`);
  }

  public updatePositions({ positions }: { positions: Position[] }): Observable<Position[]> {
    return this.httpApi
      .patch(`/talent_pools/update_positions`, null, {
        talent_pool_ids: positions.map(position => position.id),
      })
      .pipe(map(() => positions));
  }
}
